import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, RefreshCw, Bell, CheckCircle2, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { fetchAdInquiries, updateAdInquiry, fetchRankingStatus, markRankingReviewed } from "@/lib/api";

const STATUSES = ["new", "contacted", "won", "closed"];
const STATUS_COLOR = {
  new: "text-signal",
  contacted: "text-yellow-400",
  won: "text-rankup",
  closed: "text-muted-foreground",
};

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ranking, setRanking] = useState(null);

  const load = () => {
    setLoading(true);
    fetchAdInquiries()
      .then(setLeads)
      .catch(() => toast.error("Failed to load leads"))
      .finally(() => setLoading(false));
    fetchRankingStatus().then(setRanking).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const markReviewed = async () => {
    try {
      await markRankingReviewed();
      const status = await fetchRankingStatus();
      setRanking(status);
      toast.success("Rankings marked reviewed. Timer reset.");
    } catch (e) {
      toast.error("Could not update");
    }
  };

  const changeStatus = async (id, status) => {
    try {
      const updated = await updateAdInquiry(id, status);
      setLeads((ls) => ls.map((l) => (l.id === id ? updated : l)));
      toast.success(`Marked ${status}`);
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const counts = STATUSES.reduce((acc, s) => {
    acc[s] = leads.filter((l) => l.status === s).length;
    return acc;
  }, {});

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16">
      <div className="flex items-end justify-between gap-4 border-b border-white/10 pb-8">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-signal">Internal</span>
          <h1 className="font-anton text-5xl md:text-7xl uppercase tracking-tight mt-3 leading-none" data-testid="admin-title">
            Advertiser Leads
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/admin/ads"
            data-testid="admin-nav-ads"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border border-white/15 px-4 py-2 hover:border-signal hover:text-signal transition-colors"
          >
            Ad Manager
          </Link>
          <Link
            to="/admin/rankings"
            data-testid="admin-nav-rankings"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border border-white/15 px-4 py-2 hover:border-signal hover:text-signal transition-colors"
          >
            Rankings Manager
          </Link>
          <Link
            to="/admin/posts"
            data-testid="admin-nav-posts"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border border-white/15 px-4 py-2 hover:border-signal hover:text-signal transition-colors"
          >
            Manage Posts
          </Link>
          <Link
            to="/admin/community"
            data-testid="admin-nav-community"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border border-white/15 px-4 py-2 hover:border-signal hover:text-signal transition-colors"
          >
            Community Inbox
          </Link>
          <button
            data-testid="admin-refresh"
            onClick={load}
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border border-white/15 px-4 py-2 hover:border-signal hover:text-signal transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {/* Rankings refresh reminder */}
      {ranking && (
        <div
          data-testid="ranking-reminder"
          className={`mt-8 border p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
            ranking.due ? "border-signal bg-signal/10" : "border-white/10 bg-card"
          }`}
        >
          <div className="flex items-start gap-3">
            {ranking.due ? <Bell className="w-5 h-5 text-signal mt-0.5" /> : <TrendingUp className="w-5 h-5 text-muted-foreground mt-0.5" />}
            <div>
              <div className="font-anton text-lg uppercase tracking-tight">
                {ranking.due ? "Power Index sync is overdue" : "Power Index is auto-synced"}
              </div>
              <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mt-1">
                Last synced {ranking.days_since} day{ranking.days_since === 1 ? "" : "s"} ago · monthly chart sync ·{" "}
                {ranking.email_enabled ? "email alerts on" : "email alerts off (add Resend key)"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin/rankings"
              data-testid="reminder-open-rankings"
              className="font-mono text-xs uppercase tracking-widest border border-white/15 px-4 py-2 hover:border-signal hover:text-signal transition-colors"
            >
              Rankings Manager
            </Link>
            <button
              data-testid="reminder-mark-reviewed"
              onClick={markReviewed}
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest bg-signal text-white px-4 py-2 hover:bg-signal-hover transition-colors"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Mark reviewed
            </button>
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10 border-x border-b border-white/10 mt-8">
        <div className="bg-background p-5">
          <div className="font-mono-stat text-3xl">{leads.length}</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Total</div>
        </div>
        {STATUSES.map((s) => (
          <div key={s} className="bg-background p-5">
            <div className={`font-mono-stat text-3xl ${STATUS_COLOR[s]}`}>{counts[s] || 0}</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{s}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <p className="font-mono text-sm text-muted-foreground mt-10">Loading…</p>
      ) : leads.length === 0 ? (
        <p className="font-mono text-sm text-muted-foreground mt-10" data-testid="admin-empty">
          No leads yet. Inquiries from the Advertise page land here.
        </p>
      ) : (
        <div className="border-x border-b border-white/10 divide-y divide-white/10 mt-10">
          {leads.map((l) => (
            <div key={l.id} data-testid={`lead-row-${l.id}`} className="p-5 md:px-6 grid md:grid-cols-[1fr_auto] gap-4 items-start hover:bg-secondary transition-colors">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-display text-xl font-medium">{l.company}</span>
                  <span className={`font-mono text-[10px] uppercase tracking-widest ${STATUS_COLOR[l.status]}`}>● {l.status}</span>
                  {l.package && <span className="font-mono text-[10px] uppercase tracking-widest bg-secondary px-2 py-0.5">{l.package}</span>}
                  {l.budget && <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{l.budget}</span>}
                </div>
                <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mt-1">
                  {l.name} ·{" "}
                  <a href={`mailto:${l.email}`} className="text-signal hover:underline inline-flex items-center gap-1">
                    <Mail className="w-3 h-3" /> {l.email}
                  </a>{" "}
                  · {new Date(l.created_at).toLocaleDateString()}
                </div>
                {l.message && <p className="text-foreground/80 text-sm mt-2 max-w-2xl">{l.message}</p>}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    data-testid={`lead-status-${l.id}-${s}`}
                    onClick={() => changeStatus(l.id, s)}
                    className={`font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 border transition-colors ${
                      l.status === s ? "bg-signal text-white border-signal" : "border-white/15 text-foreground/60 hover:border-signal"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
